import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { useAffiliateAnalytics } from '@/hooks/affiliate/useAffiliateAnalytics';
import { TimeRangeSelector } from '@/components/analytics/TimeRangeSelector';
import { AffiliateAnalyticsOverview } from '@/components/affiliate/analytics/AffiliateAnalyticsOverview';
import { ClicksOverTimeChart } from '@/components/affiliate/analytics/ClicksOverTimeChart';
import { ClicksByCategoryChart } from '@/components/affiliate/analytics/ClicksByCategoryChart';
import { TopPartnersChart } from '@/components/affiliate/analytics/TopPartnersChart';
import { ClicksByPageChart } from '@/components/affiliate/analytics/ClicksByPageChart';

const AffiliateAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const {
    loading,
    clicksByDate,
    clicksByCategory,
    clicksByPartner,
    clicksByPage,
    totalClicks,
    previousTotalClicks,
    clicksGrowth,
    timeRange,
    setTimeRange
  } = useAffiliateAnalytics();

  const topCategory = clicksByCategory.length > 0 ? clicksByCategory[0] : null;
  const topPartner = clicksByPartner.length > 0 ? clicksByPartner[0] : null;

  return (
    <>
      <Helmet>
        <title>Affiliate Analytics - TravelDraft</title>
        <meta name="description" content="Track and analyze affiliate partner performance and revenue" />
      </Helmet>

      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Affiliate Analytics</h1>
            <p className="text-muted-foreground">Track and analyze affiliate partner performance with period-over-period comparison.</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="ml-2 text-muted-foreground">Loading analytics data...</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
              <TabsTrigger value="categories">Categories</TabsTrigger>
              <TabsTrigger value="partners">Top Partners</TabsTrigger>
              <TabsTrigger value="pages">Pages</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview">
              <AffiliateAnalyticsOverview 
                totalClicks={totalClicks}
                previousTotalClicks={previousTotalClicks}
                clicksGrowth={clicksGrowth}
                topCategory={topCategory} 
                topPartner={topPartner} 
              />
              <ClicksOverTimeChart data={clicksByDate} />
              <div className="grid gap-6 md:grid-cols-2 mt-6">
                <TopPartnersChart data={clicksByPartner.slice(0, 5)} />
                <ClicksByCategoryChart data={clicksByCategory} />
              </div>
            </TabsContent>
            
            <TabsContent value="trends">
              <ClicksOverTimeChart data={clicksByDate} />
            </TabsContent>
            
            <TabsContent value="categories">
              <ClicksByCategoryChart data={clicksByCategory} />
            </TabsContent>
            
            <TabsContent value="partners">
              <TopPartnersChart data={clicksByPartner} />
            </TabsContent>

            <TabsContent value="pages">
              <ClicksByPageChart data={clicksByPage} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </>
  );
};

export default AffiliateAnalytics;
